// /app/frontend/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
};

// MFA requerido: el usuario tiene un factor TOTP verificado pero su sesión aún es aal1
const computeMfaRequired = (session) => {
  if (!session?.access_token) return false;
  const hasVerifiedFactor = (session.user?.factors || []).some((f) => f.status === 'verified');
  if (!hasVerifiedFactor) return false;
  const aal = decodeJwtPayload(session.access_token).aal;
  return aal !== 'aal2';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setMfaRequired(computeMfaRequired(session));
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setMfaRequired(computeMfaRequired(session));
        setLoading(false);

        // Sync fuera de la cadena de notificación de supabase-js (evita deadlock del lock interno)
        if ((event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED') && session?.user && !computeMfaRequired(session)) {
          setTimeout(() => syncUserToDatabase(session.user, session.access_token), 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sync user to our users table
  const syncUserToDatabase = async (authUser, accessToken) => {
    try {
      if (!accessToken) {
        console.error('No access token available for sync');
        return false;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sync-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          user_id: authUser.id,
          email: authUser.email,
          full_name: authUser.user_metadata?.full_name || ''
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error syncing user to database:', errorData);
        return false;
      }
      
      console.log('User synced successfully');
      return true;
    } catch (error) {
      console.error('Error syncing user:', error);
      return false;
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName
        }
      }
    });
    return { data, error };
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    // El sync se dispara desde onAuthStateChange (SIGNED_IN / MFA_CHALLENGE_VERIFIED)
    return { data, error };
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/app`
      }
    });
    return { data, error };
  };

  // Sign out
  const signOut = async () => {
    try {
      // Cierra la sesión de presencia (permanencia) antes de perder el token
      await Promise.race([api.post('/presence/logout', {}, { silent: true, timeout: 3000 }), new Promise((r) => setTimeout(r, 3000))]).catch(() => {});
      // Always clear local state first
      setUser(null);
      setSession(null);
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Try to sign out from Supabase (may fail if token is invalid)
      await supabase.auth.signOut().catch(() => {});
      
      // Redirect to login
      window.location.href = '/login';
      
      return { error: null };
    } catch (error) {
      // Even if there's an error, clear everything and redirect
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
      return { error };
    }
  };

  // Reset password (send email)
  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { data, error };
  };

  // Update password
  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { data, error };
  };

  const value = {
    user,
    session,
    loading,
    mfaRequired,
    isAuthenticated: !!session,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    getAccessToken: () => session?.access_token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
